'use client';

/**
 * Friendly bird — rounder proportions, bigger eyes, customizable plumage.
 * Direct React port of the design handoff. Replaces the legacy 72x72 SVG
 * mascot with a 100x100 viewBox variant that supports palettes + accessories.
 */

import { type CSSProperties, useEffect, useState } from 'react';
import { type Accessory, BIRD_PALETTES, type BirdPalette } from './palettes.js';
import type { MascotState } from './states.js';

export interface FriendlyBirdProps {
  readonly state?: MascotState;
  readonly size?: number;
  readonly palette?: keyof typeof BIRD_PALETTES;
  readonly accessory?: Accessory;
  /** Optional override for chest color — lets tenants bring their own accent. */
  readonly accent?: string;
  readonly label?: string;
}

export function FriendlyBird({
  state = 'idle',
  size = 72,
  palette = 'robin',
  accessory = 'none',
  accent,
  label,
}: FriendlyBirdProps) {
  const [internal, setInternal] = useState<MascotState>(state);
  const [preen, setPreen] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);

  useEffect(() => {
    setInternal(state);
    if (state === 'happy') setConfettiKey((k) => k + 1);
  }, [state]);

  useEffect(() => {
    if (internal !== 'idle') return;
    const t = setInterval(
      () => {
        setPreen(true);
        setTimeout(() => setPreen(false), 800);
      },
      25000 + Math.random() * 15000,
    );
    return () => clearInterval(t);
  }, [internal]);

  const p: BirdPalette = BIRD_PALETTES[palette] ?? BIRD_PALETTES['robin']!;
  const HEAD = p.head;
  const BELLY = p.belly;
  const CHEST = accent ?? p.chest;
  const TUFT = p.tuft;
  const CHEEK = p.cheek;

  const s = internal;
  const eyesClosed = s === 'sleeping';
  const eyesHalf = s === 'thinking';
  const eyesWide = s === 'happy' || s === 'hover';
  const headTilt = s === 'hover' ? 8 : s === 'confused' ? 22 : s === 'speaking' ? 3 : 0;
  const beakOpen = s === 'speaking';
  const smile = s === 'happy' || s === 'hover';
  const blushActive = s === 'happy' || s === 'hover' || s === 'listening';
  const cheekOpacity = s === 'happy' ? 0.85 : s === 'hover' ? 0.6 : s === 'listening' ? 0.5 : 0;
  const hopping = s === 'happy';

  const gid = `belly-${p.id}-${CHEST.replace('#', '')}`;

  const wrapStyle: CSSProperties = {
    position: 'relative',
    width: size,
    height: size,
    display: 'inline-block',
    animation: hopping
      ? 'hop 700ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))'
      : 'none',
  };

  return (
    <div style={wrapStyle} aria-label={label ?? `Perch bird — ${s}`} role="img">
      {s === 'thinking' ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 50% 72%, rgba(139,122,184,0.38), transparent 55%)',
            filter: 'blur(8px)',
            pointerEvents: 'none',
          }}
        />
      ) : null}

      {s === 'listening'
        ? [0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                border: '2px solid rgba(168,213,226,0.6)',
                animation: `halo-pulse 1600ms ease-out ${i * 500}ms infinite`,
                pointerEvents: 'none',
              }}
            />
          ))
        : null}

      {s === 'speaking'
        ? [0, 1].map((i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: '40%',
                top: '52%',
                width: '20%',
                height: '20%',
                borderRadius: '50%',
                border: `2px solid ${CHEST}`,
                animation: `ring-out 800ms ease-out ${i * 400}ms infinite`,
                pointerEvents: 'none',
              }}
            />
          ))
        : null}

      {s === 'happy' ? (
        <div key={confettiKey} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {[
            { c: CHEST, dx: -22, d: 0 },
            { c: TUFT, dx: 18, d: 80 },
            { c: 'var(--perch-color-sky, #A8D5E2)', dx: -4, d: 160 },
            { c: 'var(--perch-color-leaf, #6BBF7B)', dx: 28, d: 40 },
            { c: CHEEK, dx: -30, d: 120 },
          ].map((pt, i) => (
            <div
              key={i}
              style={
                {
                  position: 'absolute',
                  left: '50%',
                  top: '30%',
                  width: 4,
                  height: 6,
                  background: pt.c,
                  borderRadius: 2,
                  ['--dx' as string]: `${pt.dx}px`,
                  animation: `confetti-drift 900ms ease-out ${pt.d}ms forwards`,
                } as CSSProperties
              }
            />
          ))}
        </div>
      ) : null}

      {s === 'sleeping' ? (
        <div style={{ position: 'absolute', left: '60%', top: '6%', pointerEvents: 'none' }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                position: 'absolute',
                fontFamily: 'Fraunces, Georgia, serif',
                fontStyle: 'italic',
                fontSize: 13,
                color: 'var(--perch-color-ink-soft, #3D4654)',
                animation: `zzz 3000ms ease-out ${i * 1000}ms infinite`,
              }}
            >
              z
            </span>
          ))}
        </div>
      ) : null}

      {s === 'confused' ? (
        <div
          style={{
            position: 'absolute',
            left: '62%',
            top: '-2%',
            background: 'var(--perch-color-paper-raised, #FFFDF5)',
            borderRadius: 8,
            padding: '2px 8px',
            boxShadow: 'var(--perch-shadow-soft, 0 2px 12px -2px rgba(15,20,25,.08))',
            fontFamily: 'Fraunces, serif',
            fontWeight: 500,
            fontSize: 14,
            color: 'var(--perch-color-ink, #0F1419)',
            animation:
              'slide-up-spring 240ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
          }}
        >
          ?
        </div>
      ) : null}

      {s === 'thinking' ? (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '0%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 4,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: HEAD,
                animation: `dot-rise 1200ms ease-in-out ${i * 400}ms infinite`,
              }}
            />
          ))}
        </div>
      ) : null}

      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        style={{
          position: 'relative',
          animation:
            s === 'idle' || s === 'hover'
              ? 'breathe 2400ms ease-in-out infinite'
              : s === 'sleeping'
                ? 'breathe 4000ms ease-in-out infinite'
                : 'none',
          transition: 'transform 240ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
          transform: s === 'hover' ? 'translateY(-2px)' : 'none',
        }}
      >
        <defs>
          <radialGradient id={gid} cx="50%" cy="55%" r="65%">
            <stop offset="0%" stopColor={BELLY} />
            <stop offset="55%" stopColor={CHEST} />
            <stop offset="100%" stopColor={CHEST} stopOpacity="0.9" />
          </radialGradient>
        </defs>

        <g
          style={{
            transformOrigin: '50px 62px',
            transition: 'transform 280ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
            animation: s === 'thinking' ? 'sway 3000ms ease-in-out infinite' : 'none',
          }}
        >
          <ellipse cx="50" cy="68" rx="26" ry="22" fill={HEAD} />
          <ellipse cx="50" cy="70" rx="19" ry="16" fill={`url(#${gid})`} />
          <ellipse cx="46" cy="66" rx="6" ry="4" fill={BELLY} opacity="0.5" />
          <path d="M 72 66 Q 86 62 84 74 Q 78 71 74 73 Z" fill={HEAD} />
          <path d="M 74 69 L 84 68" stroke={HEAD} strokeWidth="0.6" fill="none" opacity="0.5" />
          <path
            d="M 34 60 Q 40 74 50 78"
            stroke={HEAD}
            strokeWidth="0.9"
            fill="none"
            opacity="0.4"
          />
          <circle cx="33" cy="64" r="5" fill={HEAD} opacity="0.4" />
          {(s === 'happy' || s === 'sleeping') && (
            <g stroke="#C89878" strokeWidth="1.4" strokeLinecap="round" fill="none">
              <line x1="45" y1="88" x2="45" y2="92" />
              <line x1="55" y1="88" x2="55" y2="92" />
              <line x1="43" y1="92" x2="47" y2="92" />
              <line x1="53" y1="92" x2="57" y2="92" />
            </g>
          )}
        </g>

        <g
          style={{
            transformOrigin: '50px 42px',
            transform: `rotate(${headTilt}deg) ${preen ? 'translate(-8px, 6px) rotate(-30deg)' : ''} ${s === 'sleeping' ? 'translateY(8px)' : ''}`,
            transition: 'transform 280ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
          }}
        >
          <circle cx="50" cy="38" r="28" fill={HEAD} />
          <circle
            cx="32"
            cy="44"
            r="6"
            fill={CHEEK}
            opacity={blushActive ? cheekOpacity : 0.22}
            style={{ transition: 'opacity 240ms ease' }}
          />
          <circle
            cx="68"
            cy="44"
            r="6"
            fill={CHEEK}
            opacity={blushActive ? cheekOpacity : 0.22}
            style={{ transition: 'opacity 240ms ease' }}
          />

          {eyesClosed ? (
            <g stroke={BELLY} strokeWidth="1.8" strokeLinecap="round" fill="none">
              <path d="M 37 36 Q 42 39 47 36" />
              <path d="M 53 36 Q 58 39 63 36" />
            </g>
          ) : (
            <>
              <circle
                cx="42"
                cy="36"
                r="6.5"
                fill="#FBF7F0"
                style={{
                  animation: 'blink 5s ease-in-out infinite',
                  transformOrigin: '42px 36px',
                }}
              />
              <circle
                cx="58"
                cy="36"
                r="6.5"
                fill="#FBF7F0"
                style={{
                  animation: 'blink 5s ease-in-out infinite',
                  animationDelay: '0.05s',
                  transformOrigin: '58px 36px',
                }}
              />
              {eyesHalf && (
                <>
                  <path d="M 35.5 36 A 6.5 6.5 0 0 1 48.5 36 L 48.5 36 L 35.5 36 Z" fill={HEAD} />
                  <path d="M 51.5 36 A 6.5 6.5 0 0 1 64.5 36 L 64.5 36 L 51.5 36 Z" fill={HEAD} />
                </>
              )}
              <circle
                cx="42"
                cy="37.5"
                r={eyesWide ? 4 : 3.4}
                fill="#0F1419"
                style={{
                  transition: 'r 180ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
                }}
              />
              <circle
                cx="58"
                cy="37.5"
                r={eyesWide ? 4 : 3.4}
                fill="#0F1419"
                style={{
                  transition: 'r 180ms var(--perch-ease-spring, cubic-bezier(0.34,1.56,0.64,1))',
                }}
              />
              <circle cx="43.3" cy="36.2" r="1.2" fill="#FBF7F0" />
              <circle cx="59.3" cy="36.2" r="1.2" fill="#FBF7F0" />
              <circle cx="41" cy="38.5" r="0.5" fill="#FBF7F0" opacity="0.7" />
              <circle cx="57" cy="38.5" r="0.5" fill="#FBF7F0" opacity="0.7" />
            </>
          )}

          {beakOpen ? (
            <path
              d="M 47 47 Q 50 54 53 47 Q 50 50 47 47 Z"
              fill="#E8A35F"
              stroke="#C48440"
              strokeWidth="0.5"
            />
          ) : smile ? (
            <>
              <path
                d="M 47 46 Q 50 49 53 46 Q 50 48 47 46 Z"
                fill="#F0B56B"
                stroke="#C48440"
                strokeWidth="0.4"
              />
              <path
                d="M 44 50 Q 50 54 56 50"
                stroke="#C48440"
                strokeWidth="1.3"
                fill="none"
                strokeLinecap="round"
              />
            </>
          ) : (
            <path
              d="M 47 46 Q 50 50 53 46 Q 50 48 47 46 Z"
              fill="#F0B56B"
              stroke="#C48440"
              strokeWidth="0.4"
            />
          )}

          <path d="M 48 11 Q 50 5 52 11 Q 54 6 56 12 Q 52 14 48 13 Z" fill={TUFT} />
          <path d="M 50 10 L 50 14" stroke={HEAD} strokeWidth="0.8" opacity="0.4" />

          {accessory === 'beanie' && (
            <g>
              <path d="M 28 28 Q 30 10 50 8 Q 70 10 72 28 Z" fill={CHEST} />
              <circle cx="50" cy="6" r="3.5" fill={BELLY} />
              <path d="M 28 28 L 72 28" stroke={HEAD} strokeWidth="0.5" opacity="0.3" />
            </g>
          )}
          {accessory === 'leaf' && (
            <g transform="rotate(-25 58 14)">
              <path
                d="M 58 14 Q 68 8 70 18 Q 65 22 58 14 Z"
                fill="var(--perch-color-leaf, #6BBF7B)"
              />
              <path d="M 58 14 L 68 12" stroke="#2E5A3A" strokeWidth="0.6" fill="none" />
            </g>
          )}
          {accessory === 'bowtie' && (
            <g transform="translate(50 65)">
              <path d="M -8 -3 L -2 0 L -8 3 Z" fill={CHEST} />
              <path d="M 8 -3 L 2 0 L 8 3 Z" fill={CHEST} />
              <rect x="-2" y="-2" width="4" height="4" rx="1" fill="#C48440" />
            </g>
          )}
          {accessory === 'glasses' && (
            <g stroke={HEAD} strokeWidth="1.5" fill="none">
              <circle cx="42" cy="37" r="8" fill="rgba(255,255,255,0.15)" />
              <circle cx="58" cy="37" r="8" fill="rgba(255,255,255,0.15)" />
              <path d="M 50 37 L 48 37 M 50 37 L 52 37" />
            </g>
          )}
          {accessory === 'flower' && (
            <g transform="translate(30 22)">
              {[0, 72, 144, 216, 288].map((a) => (
                <ellipse
                  key={a}
                  cx="0"
                  cy="-4"
                  rx="2.2"
                  ry="3.5"
                  fill={TUFT}
                  transform={`rotate(${a})`}
                />
              ))}
              <circle cx="0" cy="0" r="1.6" fill="var(--perch-color-sun, #FFC76B)" />
            </g>
          )}
        </g>
      </svg>
    </div>
  );
}
